import { useState, useEffect, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  isSuperAdmin: boolean;
  hasStaffRole: boolean;
  isCoach: boolean;
  isLoading: boolean;
}

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    session: null,
    isSuperAdmin: false,
    hasStaffRole: false,
    isCoach: false,
    isLoading: true,
  });

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setAuthState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
        }));

        if (session?.user) {
          setTimeout(() => {
            checkRoles(session.user.id);
          }, 0);
        } else {
          setAuthState(prev => ({
            ...prev,
            isSuperAdmin: false,
            hasStaffRole: false,
            isCoach: false,
            isLoading: false,
          }));
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setAuthState(prev => ({
        ...prev,
        session,
        user: session?.user ?? null,
      }));

      if (session?.user) {
        checkRoles(session.user.id);
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkRoles = async (userId: string) => {
    try {
      // Check for any role in user_roles table
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        console.error('Error checking roles:', error);
        setAuthState(prev => ({ ...prev, isSuperAdmin: false, hasStaffRole: false, isCoach: false, isLoading: false }));
        return;
      }

      const roles = data?.map(r => r.role) || [];
      const isAdminOrMod = roles.includes('admin') || roles.includes('moderator');
      setAuthState(prev => ({
        ...prev,
        isSuperAdmin: isAdminOrMod,
        hasStaffRole: isAdminOrMod,
        isCoach: roles.includes('coach'),
        isLoading: false,
      }));

    } catch (err) {
      console.error('Error in checkRoles:', err);
      setAuthState(prev => ({ ...prev, isSuperAdmin: false, hasStaffRole: false, isCoach: false, isLoading: false }));
    }
  };

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { data, error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (!error) {
      setAuthState({
        user: null,
        session: null,
        isSuperAdmin: false,
        hasStaffRole: false,
        isCoach: false,
        isLoading: false,
      });
    }
    return { error };
  }, []);

  return {
    user: authState.user,
    session: authState.session,
    isSuperAdmin: authState.isSuperAdmin,
    hasStaffRole: authState.hasStaffRole,
    isCoach: authState.isCoach,
    isLoading: authState.isLoading,
    signIn,
    signUp,
    signOut,
  };
};
